/**
 * Approved-but-not-activated state.
 *
 * An administrator accepted the request and a code was mailed to the bound
 * address. The mail is the only place the plaintext code ever appears, so this
 * panel states where it was sent, when, and offers a resend that never issues a
 * new code and never extends the deadline.
 */

import { useTranslation } from 'react-i18next'

import type { InviteStatusResponse } from '@shared/types'

import { currentLocale } from '../../lib/i18n'
import { ErrorAlert } from '../layout/states'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { useCountdown } from '../../hooks/use-countdown'
import { useResendInvite } from '../../hooks/use-invite'
import { formatUnixSeconds } from '../../lib/format'
import { mutationErrorText } from '../../lib/mutation-error'
import { RedeemForm } from './redeem-form'

export function ApprovedPanel({
  status,
  email,
}: {
  status: InviteStatusResponse
  email: string
}) {
  const { t } = useTranslation()
  const resend = useResendInvite()
  const locale = currentLocale()
  const cooldown = useCountdown(status.resendAfterSeconds)

  // The failure is read as text rather than as an `ApiError`: a request that never
  // reached the server has no `ApiError` shape but still needs a message.
  const resendError = mutationErrorText(resend.error, t)

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('invite.approved.title')}</CardTitle>
          <CardDescription>{t('invite.approved.body', { email })}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Alert variant="success">
            <AlertTitle>{t('invite.approved.sentTitle')}</AlertTitle>
            <AlertDescription>
              {status.pendingCodeSentAt
                ? t('invite.approved.sentAt', {
                    date: formatUnixSeconds(status.pendingCodeSentAt, locale),
                  })
                : t('invite.approved.sentUnknown')}
            </AlertDescription>
          </Alert>
          <p className="text-muted-foreground text-sm">{t('invite.approved.expiryHint')}</p>

          {status.developmentCode ? (
            <Alert variant="warning">
              <AlertDescription>
                {t('invite.devCodeHint', { code: status.developmentCode })}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="self-start"
              disabled={cooldown > 0 || resend.isPending}
              onClick={() => resend.mutate()}
            >
              {cooldown > 0
                ? t('invite.approved.resendIn', { seconds: cooldown })
                : resend.isPending
                  ? t('invite.approved.resending')
                  : t('invite.approved.resend')}
            </Button>

            {resendError ? (
              <ErrorAlert message={resendError} />
            ) : null}
          </div>
        </CardContent>
      </Card>

      <RedeemForm />
    </div>
  )
}
